import React, { useState, useRef, useMemo } from 'react';
import styles from './Analytics.module.scss';
import SettingsHeader from 'pages/authenticated/layout/settingsLayout/settingsHeader/SettingsHeader';
import FilterDropdown from 'component/Custom/FilterDropdown/FilterDropdown';
import moment from 'moment';
import Button from 'component/atom/forms/button/Button';
import UserInsights from 'component/organism/analytics/user-insights/UserInsights';
import BotPerformance from 'component/organism/analytics/bot-performance/BotPerformance';
import { useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Tab from 'component/molecules/Tab/TabComponent';
import { useUserContext } from 'app-context/user/UserContext';

const Analytics = () => {
  const { id } = useParams();
  const { selectedBot } = useUserContext();

  const [isExporting, setIsExporting] = useState(false);
  const [days, setDays] = useState('7 Days');
  const today = moment().format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(
    moment().subtract(6, 'days').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(today);

  const tabRef = useRef();
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [mountExportStage, setMountExportStage] = useState(false);
  const stageRef = useRef(null);
  const stageWidth = useRef(0);

  const tabs = useMemo(
    () => [
      {
        label: 'User Insights',
        content: (
          <UserInsights
            botId={id}
            startDate={startDate}
            endDate={endDate}
            days={days}
          />
        ),
      },
      {
        label: 'Bot Performance',
        content: (
          <BotPerformance botId={id} startDate={startDate} endDate={endDate} />
        ),
      },
    ],
    [id, startDate, endDate, days]
  );

  const rightItem = (
    <div className={styles['right-item']}>
      <FilterDropdown
        days={days}
        handleSetDays={setDays}
        handleOpenCustomDates={() => {}}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        variant='secondary'
      />
      <Button label='Export to PDF' onClick={() => handleExportPDF()} />
    </div>
  );

  const handleExportPDF = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      stageWidth.current = tabRef.current?.offsetWidth || 1200;
      setMountExportStage(true);
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => setTimeout(r, 300));

      const root = stageRef.current;
      if (!root) return;

      const dcEl = root.querySelector('[data-export="daily-conversations"]');
      const mbtEl = root.querySelector('[data-export="messages-by-topic"]');
      const uaEl = root.querySelector('[data-export="user-activities"]');
      const uisEl = root.querySelector(
        '[data-export="user-insights-statistics"]'
      );
      const rrEl = root.querySelector('[data-export="resolution-rate"]');
      const usrEl = root.querySelector(
        '[data-export="user-satisfaction-rate"]'
      );
      const mwEl = root.querySelector('[data-export="most-used-workflow"]');
      const kbEl = root.querySelector(
        '[data-export="most-used-knowledge-base"]'
      );

      const HIDE = [
        'select',
        '[role="combobox"]',
        '[aria-haspopup="listbox"]',
        '[class*="select"]',
        '[class*="Select"]',
        '[class*="dropdown"]',
        '[class*="Dropdown"]',
        '[class*="react-select"]',
        '.apexcharts-toolbar',
        '[class*="download-icon"]',
        '[class*="tooltip"]',
      ].join(',');

      const capture = (el) =>
        html2canvas(el, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          onclone: (doc) => {
            doc.querySelectorAll(HIDE).forEach((n) => {
              n.style.display = 'none';
              n.style.visibility = 'hidden';
            });
          },
        });

      const capIf = (el) => (el ? capture(el) : Promise.resolve(null));

      const [dcCv, mbtCv, uaCv, uisCv, rrCv, usrCv, mwCv, kbCv] =
        await Promise.all([
          capIf(dcEl),
          capIf(mbtEl),
          capIf(uaEl),
          capIf(uisEl),
          capIf(rrEl),
          capIf(usrEl),
          capIf(mwEl),
          capIf(kbEl),
        ]);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;

      const headerText = (selectedBot?.botName || '').trim();
      const initials = headerText
        ? headerText
            .split(' ')
            .filter(Boolean)
            .map((w) => w[0])
            .join('')
            .toUpperCase()
        : '';

      const avatarSize = 8; // mm
      const gap = 3; // mm between avatar and title
      const titleFontPt = 12; // requested "12"
      const pxToPt = (px) => px * 0.75;
      const ptToMm = (pt) => pt * 0.352778;

      const dashFontPt = pxToPt(20); // 20px -> 15pt
      const sectionFontPt = pxToPt(16); // 16px -> 12pt
      const dashLineH = ptToMm(dashFontPt);
      const sectionLineH = ptToMm(sectionFontPt);

      const headerPresent = Boolean(headerText);
      const vGapRowToDash = 2; // mm
      const vGapBetweenLines = 1.5; // mm
      const vGapBottom = 4; // mm
      const avatarRowH = avatarSize;

      const headerH = headerPresent
        ? avatarRowH +
          vGapRowToDash +
          dashLineH +
          vGapBetweenLines +
          sectionLineH +
          vGapBottom
        : 0;

      const innerW = pageW - margin * 2;
      const innerH = pageH - margin * 2 - headerH;
      const contentTopY = margin + headerH;
      const vGap = 8;
      const gutter = 6;
      const halfH = (innerH - vGap) / 2;

      const addHeader = () => {
        if (!headerPresent) return;

        // avatar (purple circle) with initials
        pdf.setFillColor(128, 0, 128);
        pdf.roundedRect(
          margin,
          margin,
          avatarSize,
          avatarSize,
          avatarSize / 2,
          avatarSize / 2,
          'F'
        );

        if (initials) {
          pdf.setTextColor(255, 255, 255);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          const cx = margin + avatarSize / 2;
          const cy = margin + avatarSize / 2;
          pdf.text(initials, cx, cy, { align: 'center', baseline: 'middle' });
        }

        // bot title (left-aligned, size 12)
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(titleFontPt);
        const titleX = margin + avatarSize + gap;
        const titleY = margin + avatarSize / 2;
        pdf.text(headerText, titleX, titleY, { baseline: 'middle' });

        // "Dashboard" (20px, #000)
        const dashTopY = margin + avatarRowH + vGapRowToDash;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(dashFontPt);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Dashboard', margin, dashTopY, { baseline: 'top' });

        // "User Insights" (16px, #4E4E4E)
        const sectionTopY = dashTopY + dashLineH + vGapBetweenLines;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(sectionFontPt);
        pdf.setTextColor(78, 78, 78);
        pdf.text('User Insights', margin, sectionTopY, { baseline: 'top' });
      };

      const addImageFit = (cv, x, y, w, h) => {
        if (!cv) return;
        const ratio = Math.min(w / cv.width, h / cv.height);
        const iw = cv.width * ratio;
        const ih = cv.height * ratio;
        const ix = x + (w - iw) / 2;
        const iy = y + (h - ih) / 2;
        pdf.addImage(cv.toDataURL('image/png'), 'PNG', ix, iy, iw, ih);
      };

      const addTwoCols = (leftCv, rightCv, y, h) => {
        if (leftCv && rightCv) {
          const colW = (innerW - gutter) / 2;
          addImageFit(leftCv, margin, y, colW, h);
          addImageFit(rightCv, margin + colW + gutter, y, colW, h);
        } else if (leftCv || rightCv) {
          addImageFit(leftCv || rightCv, margin, y, innerW, h);
        }
      };

      let pagesAdded = 0;

      if (dcCv || mbtCv) {
        addHeader();
        addImageFit(dcCv, margin, contentTopY, innerW, halfH);
        addImageFit(mbtCv, margin, contentTopY + halfH + vGap, innerW, halfH);
        pagesAdded++;
      }

      if (uaCv || uisCv) {
        if (pagesAdded) pdf.addPage();
        addHeader();
        addImageFit(uaCv, margin, contentTopY, innerW, halfH);
        addImageFit(uisCv, margin, contentTopY + halfH + vGap, innerW, halfH);
        pagesAdded++;
      }

      if (rrCv || usrCv || mwCv) {
        if (pagesAdded) pdf.addPage();
        addHeader();
        const topH = Math.round((innerH - vGap) / 2);
        const bottomH = innerH - topH - vGap;
        addTwoCols(rrCv, usrCv, contentTopY, topH);
        addImageFit(mwCv, margin, contentTopY + topH + vGap, innerW, bottomH);
        pagesAdded++;
      }

      if (kbCv) {
        if (pagesAdded) pdf.addPage();
        addHeader();
        addImageFit(kbCv, margin, contentTopY, innerW, innerH);
        pagesAdded++;
      }

      if (!pagesAdded) {
        const canvas = await html2canvas(tabRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
        });
        addHeader();
        addImageFit(canvas, margin, contentTopY, innerW, innerH);
      }

      const fileName = tabs[activeTabIndex].label
        .replace(/\s+/g, '-')
        .toLowerCase();
      pdf.save(`${fileName}.pdf`);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setMountExportStage(false);
      setIsExporting(false);
    }
  };

  return (
    <div className={styles['analytics-container']}>
      <SettingsHeader title='Analytics' rightItem={rightItem} />
      <div ref={tabRef}>
        <Tab tabs={tabs} onTabChange={(i) => setActiveTabIndex(i)} />
      </div>

      {mountExportStage && (
        <div
          ref={stageRef}
          style={{
            position: 'fixed',
            left: '-100000px',
            top: 0,
            width: stageWidth.current || 1200,
            opacity: 1,
            pointerEvents: 'none',
            zIndex: -1,
          }}
        >
          <UserInsights
            botId={id}
            startDate={startDate}
            endDate={endDate}
            days={days}
          />
          <BotPerformance botId={id} startDate={startDate} endDate={endDate} />
        </div>
      )}
    </div>
  );
};

export default Analytics;
